import { AccountError, type Account } from './account';

/**
 * Who the player is and what they may do — in one place, not spread over the menus.
 *
 * Authentication: `state` (below). Authorization: four levels, each needing the one before it —
 *   none     solo, the collection, rules, graphics settings
 *   account  a session (the web's quiet guest, or the Telegram user): profile, progress, a code for another device
 *   player   an account with a nick: co-op, a random ally, an own row in the ranking
 *   payer    the Telegram user, inside the Mini App: Stars purchases
 * `can()` answers at once (to show or explain a button), `require()` gets there: signs in, asks for
 * the nick, or refuses with a reason the screen can put into words.
 */
export type Level = 'none' | 'account' | 'player' | 'payer';
export type AuthState = 'anonymous' | 'signing' | 'guest' | 'telegram' | 'offline' | 'relaunch';
export type Refusal = 'offline' | 'relaunch' | 'not_telegram' | 'cancelled';

export class AccessError extends Error {
  constructor(readonly reason: Refusal) { super(reason); }
}

/** What each refusal means, in the player's words. */
export const REFUSAL: Record<Refusal, string> = {
  offline: 'Сервер недоступний. Соло працює й так.',
  relaunch: 'Сесія Telegram застаріла. Відкрийте гру з Telegram ще раз.',
  not_telegram: 'Підтримати гру можна в Telegram-версії.',
  cancelled: '',
};

export class Session {
  /** Asks for the nick (the onboarding's last step, or the nick screen); resolves with the accepted nick. */
  askNick: (session: Session) => Promise<string> = () => Promise.reject(new AccessError('cancelled'));

  /** The nick being asked for: two quick taps on the way online share one step, not two. */
  private asking: Promise<string> | null = null;

  constructor(readonly account: Account) {}

  get state(): AuthState {
    const a = this.account;
    if (a.state === 'signed') return this.linkedTelegram ? 'telegram' : 'guest';
    return a.state;
  }
  get profile() { return this.account.profile; }
  get nick(): string | null { return this.account.profile?.account.nick ?? null; }
  /** The account has Telegram among its sign-ins. */
  get linkedTelegram() { return !!this.account.profile?.identities?.includes('telegram'); }

  /** The first moments: Telegram signs in at once, the web only if it already has a session (no guest per visit). */
  start(): Promise<void> {
    if (!this.account.inTelegram && !this.account.token) return Promise.resolve();
    return this.account.signIn().then(() => {}, () => { /* offline or relaunch: the state says which */ });
  }

  can(level: Level): boolean {
    if (level === 'none') return true;
    const signed = this.account.state === 'signed';
    if (level === 'account') return signed;
    if (level === 'player') return signed && this.nick !== null;
    return signed && this.linkedTelegram && this.account.inTelegram;
  }

  /** Gets to `level`, or throws AccessError. `player` may open the nick step. */
  async require(level: Level): Promise<void> {
    if (level === 'none') return;
    if (this.account.state !== 'signed') {
      try { await this.account.signIn(); } catch (e) {
        throw new AccessError(e instanceof AccountError && e.code === 'relaunch' ? 'relaunch' : 'offline');
      }
    }
    if (level === 'payer' && !(this.linkedTelegram && this.account.inTelegram)) throw new AccessError('not_telegram');
    if (level === 'player' && this.nick === null) {
      this.asking ??= this.askNick(this).finally(() => { this.asking = null; });
      await this.asking;
    }
  }

  /** Called on every change of the account or the sign-in; returns the unsubscribe. */
  subscribe(fn: () => void) { return this.account.subscribe(fn); }
}
